/**
 * Lõi nén lại ảnh — chạy được CẢ trên trang lẫn trong luồng ngầm (Web Worker),
 * nên không đụng `document` hay `window`. `jpeg-recompress.ts` là cửa dùng; tệp
 * luồng ngầm `jpeg-recompress.worker.ts` gọi thẳng `reencode`.
 *
 * Tách riêng vì luồng ngầm KHÔNG được nhập `jpeg-recompress.ts`: tệp đó dựng
 * luồng ngầm bằng `new Worker(new URL(...))`, mà Vite đóng gói luồng ngầm có
 * dòng ấy là tự lồng chính nó.
 */
import { findExifSegment, insertExifSegment, jpegFrameSize, prepareExifForCopy, readOrientation } from "./jpeg-exif";

/**
 * Chất lượng mã hoá. Người quyết định: 92 — cao hơn thang thường dùng cho ảnh
 * web, cố ý, vì bản này là bản gốc mà hậu kỳ sẽ mở ra sửa.
 *
 * Hệ quả cần biết: ảnh máy ảnh xuất ra vốn đã ở mức tương đương q≈90-92, nên
 * mã hoá lại GIỮ NGUYÊN độ phân giải có thể cho ra tệp không nhỏ hơn gốc. Khi
 * đó `recompressJpeg` giữ bản gốc (xem guard cuối hàm) và không tiết kiệm
 * được gì. Băng thông chỉ thật sự giảm khi truyền `maxEdge`.
 */
export const QUALITY = 92;

/** Ảnh nhỏ hơn ngưỡng này thì không đụng vào — mã hoá lại chỉ làm xấu đi. */
export const SKIP_BELOW_BYTES = 500 * 1024;

/**
 * Trần điểm ảnh khi giải mã, khớp `AlbumMaxDecodedPixels` của máy chủ
 * (photosession/albumupload.go — đổi ở đây thì đổi cả bên đó). Ảnh vượt trần
 * thì máy chủ từ chối, nên nén ở đây cũng vô ích — trả gốc để người dùng nhận
 * đúng câu lỗi của máy chủ thay vì một tấm đã bị ta xử lý nửa vời.
 *
 * Đây KHÔNG phải hàng rào chặn ảnh bom nén (hàng rào thật nằm ở máy chủ, nơi
 * kẻ gửi không sửa được); ở đây nó chỉ giữ cho máy nhân viên khỏi phải giải mã
 * một tấm mà máy chủ sẽ từ chối ngay sau đó.
 *
 * 200 MP từ 03/09/2026, nới từ 24 MP cùng lượt gỡ trần dung lượng.
 */
export const MAX_DECODED_PIXELS = 200_000_000;

/**
 * Bộ tham số MozJPEG. Đọc song song với `cjpeg`:
 *
 *   quality           -quality 92
 *   progressive       -progressive   (ảnh to hiện dần, nhẹ hơn baseline)
 *   optimize_coding   -optimize      (bảng Huffman riêng cho từng ảnh)
 *   trellis_*         -tune-ssim     (chỗ MozJPEG ăn tiền so với libjpeg thường)
 *   chroma_subsample  -sample 2x2    (4:2:0)
 *
 * `auto_subsample: false` + `chroma_subsample: 2` là nói thẳng 4:2:0 thay vì
 * để encoder tự đoán theo quality — ở thang chất lượng cao nó tự chọn 4:4:4,
 * tệp phình ~25% mà mắt không thấy khác.
 */
export const ENCODE_OPTIONS = {
  quality: QUALITY,
  progressive: true,
  optimize_coding: true,
  trellis_multipass: true,
  trellis_opt_zero: true,
  trellis_opt_table: true,
  trellis_loops: 1,
  auto_subsample: false,
  chroma_subsample: 2,
} as const;

/**
 * Phần đầu tệp đọc để lấy EXIF + kích thước khung. Đoạn EXIF tối đa 64 KB và
 * nằm trước dữ liệu ảnh; 512 KB đủ cả khi máy ảnh nhét thêm hồ sơ màu / ảnh MPF.
 */
const HEAD_BYTES = 512 * 1024;

/**
 * Kích thước đích, giữ nguyên tỉ lệ. `max` là 0 hoặc không truyền → giữ
 * nguyên. Ảnh vốn nhỏ hơn `max` cũng giữ nguyên (không phóng to).
 *
 * Hàm thuần tuý, tách riêng để kiểm được mà không cần canvas — thứ jsdom
 * không có.
 */
export function computeTargetSize(
  width: number,
  height: number,
  max = 0,
): { width: number; height: number } {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return { width: 0, height: 0 };
  }
  const w = Math.round(width);
  const h = Math.round(height);
  if (max <= 0) return { width: w, height: h };
  const longest = Math.max(w, h);
  if (longest <= max) return { width: w, height: h };
  const ratio = max / longest;
  return {
    width: Math.max(1, Math.round(w * ratio)),
    height: Math.max(1, Math.round(h * ratio)),
  };
}

type Context2D = {
  drawImage(image: ImageBitmap, dx: number, dy: number, dw: number, dh: number): void;
  getImageData(sx: number, sy: number, sw: number, sh: number): ImageData;
};

export type ReencodeDeps = {
  /** Khung vẽ cỡ w×h: `<canvas>` trên trang, `OffscreenCanvas` trong luồng ngầm. */
  getContext: (width: number, height: number) => Context2D | null;
  encode: (pixels: ImageData, options: typeof ENCODE_OPTIONS) => Promise<ArrayBuffer>;
};

/**
 * Bản EXIF để gắn vào bản nén; `null` = không gắn. Trình duyệt giải mã ảnh có cờ
 * xoay 5–8 thì đổi chiều rộng/cao: so với khung ghi trong tệp là biết điểm ảnh
 * ĐÃ xoay (cờ về 1) hay CHƯA (giữ cờ). Không phân định được thì thôi gắn —
 * gắn sai cờ là ảnh nằm ngang.
 */
function exifForCopy(head: Uint8Array, decoded: { width: number; height: number }, out: { width: number; height: number }) {
  const segment = findExifSegment(head);
  if (!segment) return null;
  let resetOrientation = true;
  if (readOrientation(segment) >= 5) {
    const frame = jpegFrameSize(head);
    if (!frame || frame.width === frame.height) return null;
    const rotated = decoded.width === frame.height && decoded.height === frame.width;
    const raw = decoded.width === frame.width && decoded.height === frame.height;
    if (!rotated && !raw) return null;
    resetOrientation = rotated;
  }
  return prepareExifForCopy(segment, { resetOrientation, width: out.width, height: out.height });
}

/**
 * Giải mã → vẽ → mã hoá MozJPEG → gắn lại EXIF của ảnh gốc. `null` = không nén
 * được (ảnh vượt trần, thiếu khung vẽ, lỗi bất kỳ) — người gọi giữ ảnh gốc.
 */
export async function reencode(file: Blob, maxEdge: number, deps: ReencodeDeps): Promise<ArrayBuffer | null> {
  let bitmap: ImageBitmap | undefined;
  try {
    bitmap = await createImageBitmap(file);

    // Trần điểm ảnh tính trên ảnh GỐC, không phải ảnh sau khi thu: máy chủ
    // xem xét bản ta gửi lên, mà nếu có maxEdge thì bản gửi đã nằm dưới trần.
    // Chỗ này chặn cảnh giải mã một quả bom nén ngay trên máy nhân viên.
    if (bitmap.width * bitmap.height > MAX_DECODED_PIXELS) return null;

    const { width, height } = computeTargetSize(bitmap.width, bitmap.height, maxEdge);
    if (width === 0 || height === 0) return null;

    const ctx = deps.getContext(width, height);
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, width, height);
    const pixels = ctx.getImageData(0, 0, width, height);
    const encoded = new Uint8Array(await deps.encode(pixels, ENCODE_OPTIONS));

    const head = new Uint8Array(await file.slice(0, HEAD_BYTES).arrayBuffer());
    const exif = exifForCopy(head, bitmap, { width, height });
    const withExif = exif ? insertExifSegment(encoded, exif) : null;
    if (!withExif) return encoded.buffer;
    return withExif.buffer;
  } catch {
    return null;
  } finally {
    bitmap?.close?.();
  }
}
